import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../userContext';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { 
  FaStar, FaCrown, FaGift, FaCheck, FaSpinner, 
  FaArrowRight, FaFire, FaLock, FaUnlock, FaCreditCard,
  FaTimes, FaCheckCircle
} from 'react-icons/fa';
import CheckoutBrick from '../components/CheckoutBrick';
import './PremiumPlans.css';

function PremiumPlans() {
  const { user } = useContext(UserContext);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [error, setError] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [paymentPending, setPaymentPending] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      
      // Buscar apenas planos ativos
      const q = query(
        collection(db, 'planos_assinatura'),
        where('isActive', '==', true),
        orderBy('displayOrder', 'asc')
      );
      
      const snapshot = await getDocs(q);
      const plansData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setPlans(plansData);
      
      // Auto-selecionar plano em destaque ou o primeiro premium
      const highlightedPlan = plansData.find(plan => plan.isHighlighted);
      const firstPremium = plansData.find(plan => plan.planType === 'premium');
      setSelectedPlan(highlightedPlan?.id || firstPremium?.id || plansData[0]?.id);
      
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
      setError('Erro ao carregar planos de assinatura');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return parseFloat(price).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const getPlanIcon = (planType) => {
    switch (planType) {
      case 'free':
        return <FaGift className="plan-icon free" />;
      case 'premium':
        return <FaStar className="plan-icon premium" />;
      case 'enterprise':
        return <FaCrown className="plan-icon enterprise" />;
      default:
        return <FaStar className="plan-icon premium" />;
    }
  };

  const getPlanTypeLabel = (planType) => {
    switch (planType) {
      case 'free':
        return 'Gratuito';
      case 'premium':
        return 'Premium';
      case 'enterprise':
        return 'Enterprise';
      default:
        return 'Premium';
    }
  };

  const formatDuration = (duration) => {
    const durationsMap = {
      'mensal': 'mês',
      'trimestral': '3 meses',
      'semestral': '6 meses',
      'anual': 'ano',
      'vitalicio': 'vitalício'
    };
    return durationsMap[duration] || duration;
  };

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
  };

  const handleSubscribe = (plan) => {
    if (!user) {
      // Redirecionar para login se não estiver logado
      window.location.href = '/login';
      return;
    }

    if (plan.planType === 'free') {
      alert('Você já tem acesso ao plano gratuito!');
      return;
    }

    // Abrir modal de pagamento
    setPaymentPlan(plan);
    setShowPayment(true);
    setPaymentError('');
    setPaymentSuccess(null);
    setPaymentPending(null);
  };

  const handlePaymentSuccess = (paymentData) => {
    console.log('🎉 Pagamento aprovado:', paymentData);
    setPaymentError('');
    setPaymentPending(null);
    
    setPaymentSuccess({
      paymentId: paymentData.id,
      plan: paymentPlan,
      amount: paymentData.transaction_amount,
      method: paymentData.payment_method_id
    });
    
    // Fechar modal após 4 segundos e ir para home
    setTimeout(() => {
      setShowPayment(false);
      window.location.href = '/'; // Redirecionar para home
    }, 4000);
  };

  const handlePaymentCreated = (paymentData) => {
    if (paymentData?.status === 'approved') {
      handlePaymentSuccess(paymentData);
      return;
    }

    if (paymentData?.status === 'pending' || paymentData?.status === 'in_process') {
      setPaymentError('');
      setPaymentPending({
        paymentId: paymentData.id,
        status: paymentData.status,
        statusDetail: paymentData.status_detail || ''
      });
      setIsProcessing(false);
      return;
    }

    setPaymentError('Pagamento criado, mas retornou um status inesperado. Confira novamente em instantes.');
    setIsProcessing(false);
  };

  const handlePaymentError = (error) => {
    console.error('❌ Erro no pagamento:', error);
    setPaymentPending(null);
    setPaymentError(error);
    setIsProcessing(false);
  };

  const closePaymentModal = () => {
    if (!isProcessing) {
      setShowPayment(false);
      setPaymentPlan(null);
      setPaymentError('');
      setPaymentSuccess(null);
      setPaymentPending(null);
    }
  };

  const isCurrentUserPlan = (plan) => {
    if (!user) return false;
    if (plan.planType === 'free' && user.plano === 'Free') return true;
    return user.plano === plan.name;
  };

  // Preparar dados do pagador para o Mercado Pago
  const getPayerData = () => {
    if (!user) return {};
    
    const nameParts = (user.displayName || user.name || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    const rawDocument =
      user.cnpj ||
      user.CNPJ ||
      user.cpf ||
      user.CPF ||
      user.document ||
      user.documento ||
      '';
    const normalizedDocument = String(rawDocument).replace(/\D/g, '');
    const documentType =
      normalizedDocument.length === 14
        ? 'CNPJ'
        : normalizedDocument.length === 11
          ? 'CPF'
          : (user.cnpj || user.CNPJ ? 'CNPJ' : 'CPF');

    return {
      email: user.email || '',
      first_name: firstName,
      last_name: lastName,
      identification: {
        type: documentType,
        number: normalizedDocument
      }
    };
  };

  if (loading) {
    return (
      <div className="premium-container">
        <div className="loading-state">
          <FaSpinner className="spinner" />
          <h3>Carregando planos...</h3>
          <p>Aguarde enquanto buscamos as melhores opções para você</p>
        </div>
      </div>
    );
  }

  if (error || plans.length === 0) {
    return (
      <div className="premium-container">
        <div className="error-state">
          <FaLock className="error-icon" />
          <h3>Ops! Algo deu errado</h3>
          <p>{error || 'Nenhum plano disponível no momento'}</p>
          <button onClick={fetchPlans} className="retry-btn">
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="premium-container">
      <div className="premium-header">
        <h1 className="premium-title">
          Escolha o plano ideal para você
        </h1>
        <p className="premium-subtitle">
          Desbloqueie todo o potencial da EAD Conect com nossos planos premium
        </p>
        
        {user && (
          <div className="current-plan-info">
            <FaUnlock className="unlock-icon" />
            <span>Seu plano atual: <strong>{user.plano}</strong></span>
          </div>
        )}
      </div>

      <div className="plans-grid">
        {plans.map((plan) => {
          const isSelected = selectedPlan === plan.id;
          const isHighlighted = plan.isHighlighted;
          const isCurrent = isCurrentUserPlan(plan);
          
          return (
            <div
              key={plan.id}
              className={`plan-card ${isSelected ? 'selected' : ''} ${isHighlighted ? 'highlighted' : ''} ${isCurrent ? 'current' : ''}`}
              onClick={() => handlePlanSelect(plan.id)}
              style={{ borderColor: plan.color }}
            >
              {isHighlighted && (
                <div className="plan-badge" style={{ backgroundColor: plan.color }}>
                  <FaFire className="badge-icon" />
                  Mais Popular
                </div>
              )}
              
              {isCurrent && (
                <div className="current-badge">
                  <FaCheck className="badge-icon" />
                  Seu Plano Atual
                </div>
              )}

              <div className="plan-header">
                <div className="plan-type-container">
                  {getPlanIcon(plan.planType)}
                  <h3 className="plan-type" style={{ color: plan.color }}>
                    {plan.name}
                  </h3>
                </div>
                
                <div className="plan-pricing">
                  {plan.originalPrice && plan.originalPrice > plan.price && (
                    <div className="original-price">
                      {formatPrice(plan.originalPrice)}
                    </div>
                  )}
                  
                  <div className="current-price">
                    {plan.planType === 'free' || plan.price === 0 ? (
                      <span className="free-price">Grátis</span>
                    ) : (
                      <>
                        <span className="price-value">{formatPrice(plan.price)}</span>
                        <span className="price-period">/{formatDuration(plan.duration)}</span>
                      </>
                    )}
                  </div>
                  
                  {plan.discount > 0 && (
                    <div className="discount-badge">
                      -{plan.discount}% OFF
                    </div>
                  )}
                </div>
              </div>

              <div className="plan-description">
                <p>{plan.description}</p>
              </div>

              <div className="plan-features">
                <h4>O que está incluído:</h4>
                <ul>
                  {plan.features?.map((feature, index) => (
                    <li key={index} className="feature-item">
                      <FaCheck className="check-icon" style={{ color: plan.color }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="plan-footer">
                {isCurrent ? (
                  <button className="plan-button current-plan-btn" disabled>
                    <FaCheck className="btn-icon" />
                    Plano Atual
                  </button>
                ) : (
                  <button
                    className={`plan-button ${isSelected ? 'selected' : 'outline'}`}
                    style={{
                      backgroundColor: isSelected ? plan.color : 'transparent',
                      borderColor: plan.color,
                      color: isSelected ? 'white' : plan.color
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSubscribe(plan);
                    }}
                  >
                    {plan.planType === 'free' ? (
                      <>
                        <FaGift className="btn-icon" />
                        Começar Grátis
                      </>
                    ) : (
                      <>
                        <FaCreditCard className="btn-icon" />
                        {isSelected ? 'Assinar Agora' : 'Escolher Plano'}
                      </>
                    )}
                  </button>
                )}
                
                {plan.planType !== 'free' && !isCurrent && (
                  <div className="plan-guarantee">
                    <small>Garantia de 7 dias ou seu dinheiro de volta</small>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="premium-footer">
        <div className="security-info">
          <FaLock className="security-icon" />
          <span>Pagamento 100% seguro e protegido</span>
        </div>
        
        <div className="help-info">
          <p>
            Precisa de ajuda para escolher? 
            <a href="/perguntas-frequentes" className="help-link">
              Confira nossas perguntas frequentes
            </a>
          </p>
        </div>
      </div>

      {/* Modal de Pagamento */}
      {showPayment && paymentPlan && (
        <div className="payment-modal-overlay" onClick={closePaymentModal}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="payment-modal-header">
              <h2>
                <FaCreditCard style={{ marginRight: '10px', color: '#007bff' }} />
                Finalizar Assinatura
              </h2>
              <button 
                className="close-modal-btn" 
                onClick={closePaymentModal}
                disabled={isProcessing}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="payment-modal-content">
              {paymentSuccess ? (
                <div className="payment-success">
                  <div className="success-icon">
                    <FaCheckCircle />
                  </div>
                  <h3>🎉 Pagamento Aprovado!</h3>
                  <p>
                    Parabéns! Sua assinatura do plano <strong>{paymentSuccess.plan.name}</strong> foi ativada com sucesso.
                  </p>
                  <div className="success-details">
                    <div className="success-detail-item">
                      <strong>💳 ID do Pagamento:</strong> {paymentSuccess.paymentId}
                    </div>
                    <div className="success-detail-item">
                      <strong>💰 Valor Pago:</strong> {formatPrice(paymentSuccess.amount)}
                    </div>
                    <div className="success-detail-item">
                      <strong>🎯 Plano Ativado:</strong> {paymentSuccess.plan.name}
                    </div>
                  </div>
                  <div className="success-benefits">
                    <h4>🚀 Agora você tem acesso a:</h4>
                    <ul>
                      {paymentSuccess.plan.features?.slice(0, 3).map((feature, index) => (
                        <li key={index}>
                          <FaCheckCircle className="benefit-check" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="success-redirect">
                    🏠 Redirecionando para a página inicial em alguns segundos...
                  </p>
                </div>
              ) : (
                <>
                  

                  {paymentError && (
                    <div className="payment-error">
                      <p>{paymentError}</p>
                    </div>
                  )}

                  {paymentPending && (
                    <div className="payment-pending">
                      <p>
                        Seu pagamento foi recebido e está em análise pelo emissor do cartão.
                        Assim que houver confirmação, sua assinatura será ativada automaticamente.
                      </p>
                      <p>
                        <strong>ID do pagamento:</strong> {paymentPending.paymentId}
                        {paymentPending.statusDetail ? ` (${paymentPending.statusDetail})` : ''}
                      </p>
                    </div>
                  )}

                  <div className="payment-form">
                    <CheckoutBrick
                      amount={parseFloat(paymentPlan.price)}
                      payer={getPayerData()}
                      planData={paymentPlan}
                      onSuccess={handlePaymentSuccess}
                      onPaymentCreated={handlePaymentCreated}
                      onError={handlePaymentError}
                      setIsSubmitting={setIsProcessing}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PremiumPlans;
